import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import useResourceLoader from '../../util/useResourceLoader';
import DashboardLoadFailed from './DashboardLoadFailed';
import { useCallback, useEffect, useState, useRef } from 'react';
import ReportsApi from '../../api/ReportsApi';
import ProgressiveBackoff from '../../util/ProgressiveBackoff';
import { useDarkMode, BG, SURFACE, RAISED, BORDER, TEXT, MUTED, ACCENT, SUCCESS, WARNING, DANGER, INFO } from '../../context/DarkModeContext';
import { Trans } from '@lingui/react/macro';
import './DashboardLoadingSkeleton.css';

// Fit-to-height tuning. HEIGHT_TOLERANCE absorbs the self-referential jitter from the embedded
// dashboard's full-iframe-height elements (so the auto-resize settles instead of growing forever);
// MAX_FIT_HEIGHT is a hard backstop on the iframe height.
const HEIGHT_TOLERANCE = 24;
const MAX_FIT_HEIGHT = 5000;

// Same backend origin index.html already fetches template-files from (window.uwe_template,
// favicon) — proven reachable cross-origin with no extra CORS/auth work, so the embedded
// dashboard's antd theme is reconstructed here client-side from the same source, instead of
// exposing a new endpoint on the Superset/reporting-backend side.
const UWE_BACKEND_ORIGIN = new URL(import.meta.env.VITE_APP_API_ENDPOINT, document.location).origin;

// A CSS length like '4px' -> 4; antd token values are plain numbers (px assumed).
function toPx(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

async function fetchLogoDataUri() {
  try {
    const res = await fetch(`${UWE_BACKEND_ORIGIN}/template-files/logo.png`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

// Mirrors uwereportingbackend/theme.py's _build_animated_spinner_svg: two colored squares
// rotate behind the logo while the whole group flips 0deg -> 90deg. Needs the logo embedded as
// a data URI (not linked by URL) because an externally-referenced <image> inside an SVG used as
// an <img> src won't load.
function buildBrandSpinnerSvg(primary, secondary, logoDataUri) {
  if (!logoDataUri) return undefined;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="50" height="50">
  <style>
    @keyframes l4Rotate { 0% { transform: rotate(0deg); } 15% { transform: rotate(0deg); } 50% { transform: rotate(90deg); } 75% { transform: rotate(90deg); } 100% { transform: rotate(90deg); } }
    // @keyframes l4LayerBack { 0% { transform: rotate(0deg); } 25% { transform: rotate(30deg); } 45% { transform: rotate(0deg); } 100% { transform: rotate(0deg); } }
    // @keyframes l4LayerFront { 0% { transform: rotate(0deg); } 25% { transform: rotate(60deg); } 45% { transform: rotate(0deg); } 100% { transform: rotate(0deg); } }
    .l4-group { transform-origin: 50px 50px; animation: l4Rotate 1.4s infinite ease-in-out; }
    // .l4-back { transform-origin: 50px 50px; animation: l4LayerBack 1.4s infinite ease-in-out; }
    // .l4-front { transform-origin: 50px 50px; animation: l4LayerFront 1.4s infinite ease-in-out; }
  </style>
  <g class="l4-group">
    // <rect class="l4-back" x="0" y="0" width="100" height="100" fill="${primary}" opacity="0.5" />
    // <rect class="l4-front" x="0" y="0" width="100" height="100" fill="${secondary}" opacity="0.4" />
    <image href="${logoDataUri}" x="0" y="0" width="100" height="100" />
  </g>
</svg>`;
}

const LOGO_URL = `${UWE_BACKEND_ORIGIN}/template-files/logo.png`;
// theme.py's brandAppName/brandLogoAlt is Superset's own APP_NAME env var (e.g. "UWE Reporting
// Backend"), which isn't reachable client-side — OCIF's own app name is the closest stand-in
// available here, so this is a best-effort approximation, not an exact match.
const BRAND_APP_NAME = import.meta.env.VITE_APP_NAME;

// Static fields from theme.py's get_default_theme() that don't depend on brand colors — kept in
// exact parity (including its literal "Montserrat" fontFamily, which doesn't actually match
// OCIF's own Inter — that's a pre-existing mismatch in theme.py itself, not something fixed here,
// since the ask was to mirror theme.py's theme as closely as possible).
const SHARED_TOKEN_FIELDS = {
  controlHeight: 38,
  brandAppName: BRAND_APP_NAME,
  brandLogoAlt: BRAND_APP_NAME,
  brandLogoUrl: LOGO_URL,
  brandLogoMargin: '18px 0',
  brandLogoHref: '/',
  brandLogoHeight: '24px',
  brandSpinnerUrl: LOGO_URL,
  fontUrls: ['https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap'],
  fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
  fontFamilyCode: "'IBM Plex Mono', 'Courier New', monospace",
  transitionTiming: 0.3,
  brandIconMaxWidth: 37,
  fontSizeXS: '8',
  fontSizeXXL: '28',
  fontWeightNormal: '400',
  fontWeightLight: '300',
  fontWeightStrong: '500',
  fontWeightBold: '700',
  fontSize: 14,
  lineHeight: 1.5,
  borderRadius: 4,
  borderRadiusLG: 8,
  borderRadiusSM: 2,
  fontSizeHeading1: 36,
  fontSizeHeading2: 29,
  fontSizeHeading3: 25,
  fontSizeHeading4: 22,
  fontSizeHeading5: 18,
};

// Component-level overrides from theme.py's get_default_theme(), same non-color-dependent split.
const SHARED_COMPONENTS = {
  Input: { borderRadius: 4, paddingBlock: 8, paddingInline: 12, fontSize: 14, controlOutlineWidth: 4, controlHeight: 38 },
  Select: { borderRadius: 4, controlHeight: 40, fontSize: 14 },
  Button: { fontWeight: 400, fontSize: 14, borderRadius: 4, paddingBlock: 6, paddingInline: 12, controlHeight: 32 },
  Form: { labelFontSize: 13, itemMarginBottom: 8 },
  Typography: { fontWeightStrong: 500, titleMarginBottom: '0.5em' },
  DatePicker: { cellHeight: 38 },
};

// Light variant, built from OCIF's own already-loaded tenant theme (src/styles/_variables.css.js,
// itself sourced from the same /template-files/style.json this reconstructs from) — reuses the
// "uwe" antd algorithm already registered in the Superset plugin bundle for the color-ramp math.
function buildLightThemeDefault(uweTheme, logoDataUri) {
  return {
    algorithm: 'uwe',
    token: {
      ...SHARED_TOKEN_FIELDS,
      brandSpinnerSvg: buildBrandSpinnerSvg(uweTheme.primaryMain, uweTheme.secondaryMain, logoDataUri),
      colorPrimary: uweTheme.primaryMain,
      colorLink: uweTheme.secondaryMain,
      colorError: uweTheme.dangerMain,
      colorWarning: uweTheme.warningMain,
      colorSuccess: uweTheme.successMain,
      colorInfo: uweTheme.infoMain,
      colorEditorSelection: '#fff5cf',
      colorBorder: '#bdbdbd',
      colorBorderSecondary: '#e0e0e0',
      colorTextPlaceholder: '#bdbdbd',
      colorTextDisabled: '#616161',
      colorBgContainerDisabled: '#f5f5f5',
    },
    components: {
      ...SHARED_COMPONENTS,
      Input: {
        ...SHARED_COMPONENTS.Input,
        colorBorder: '#bdbdbd',
        activeBorderColor: uweTheme.primaryMain,
        hoverBorderColor: uweTheme.primaryMain,
        controlOutline: 'rgba(0,0,0,0.1)',
        colorTextPlaceholder: '#bdbdbd',
        colorTextDisabled: '#616161',
        colorBgContainerDisabled: '#f5f5f5',
      },
      Select: { ...SHARED_COMPONENTS.Select, colorBorder: '#cccccc' },
      Form: { ...SHARED_COMPONENTS.Form, labelColor: '#212121', labelRequiredMarkColor: uweTheme.dangerMain },
      DatePicker: { ...SHARED_COMPONENTS.DatePicker },
    },
  };
}

// Dark variant — antd's own built-in "dark" algorithm as a base, with OCIF's actual dark palette
// (DarkModeContext.tsx) layered on top for every color-dependent field theme.py has, so it reads
// as the same dark mode rather than a generic one.
function buildDarkThemeDefault(logoDataUri) {
  return {
    algorithm: 'dark',
    token: {
      ...SHARED_TOKEN_FIELDS,
      brandSpinnerSvg: buildBrandSpinnerSvg(ACCENT, RAISED, logoDataUri),
      colorPrimary: ACCENT,
      colorLink: ACCENT,
      colorError: DANGER,
      colorWarning: WARNING,
      colorSuccess: SUCCESS,
      colorInfo: INFO,
      colorBgBase: BG,
      colorBgContainer: SURFACE,
      colorBgElevated: RAISED,
      colorBorder: BORDER,
      colorBorderSecondary: BORDER,
      colorText: TEXT,
      colorTextSecondary: MUTED,
      colorEditorSelection: RAISED, // no dark counterpart in OCIF's palette — closest neutral surface tone
      colorTextPlaceholder: MUTED,
      colorTextDisabled: MUTED,
      colorBgContainerDisabled: RAISED,
    },
    components: {
      ...SHARED_COMPONENTS,
      Input: {
        ...SHARED_COMPONENTS.Input,
        colorBorder: BORDER,
        activeBorderColor: ACCENT,
        hoverBorderColor: ACCENT,
        controlOutline: 'rgba(255,255,255,0.15)', // theme.py's rgba(0,0,0,0.1) ring would be invisible on a dark background
        colorTextPlaceholder: MUTED,
        colorTextDisabled: MUTED,
        colorBgContainerDisabled: RAISED,
      },
      Select: { ...SHARED_COMPONENTS.Select, colorBorder: BORDER },
      Form: { ...SHARED_COMPONENTS.Form, labelColor: TEXT, labelRequiredMarkColor: DANGER },
    },
  };
}

/**
 * Blueprint-style illustration shown while the dashboard is being fetched/embedded: the same
 * hand-drawn-line technique as WorkpageShellSkeleton's BuildingBlueprint (rects/paths use
 * `pathLength={100}` so `stroke-dasharray`/`stroke-dashoffset` can animate a 0-100 draw-in
 * regardless of each shape's actual perimeter), but sketching abstract charts — a bar chart, a
 * trendline, a donut, a horizontal bar list — instead of app furniture.
 */
function DashboardBlueprint() {
  return (
    <svg viewBox="0 0 240 150" className="dashboard-blueprint-illustration" aria-hidden="true">
      <defs>
        <pattern id="dashboard-blueprint-grid" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="240" height="150" fill="url(#dashboard-blueprint-grid)" className="dashboard-blueprint-grid-bg" />

      {/* Dashboard canvas frame */}
      <rect x="10" y="10" width="220" height="130" rx="4" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-frame" />

      {/* Bar chart panel */}
      <rect x="20" y="20" width="80" height="54" rx="2" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-panel-bars" />
      <rect x="30" y="58" width="8" height="10" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-bar-1" />
      <rect x="43" y="48" width="8" height="20" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-bar-2" />
      <rect x="56" y="38" width="8" height="30" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-bar-3" />
      <rect x="69" y="52" width="8" height="16" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-bar-4" />
      <rect x="82" y="44" width="8" height="24" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-bar-5" />

      {/* Line/trend chart panel */}
      <rect x="110" y="20" width="110" height="54" rx="2" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-panel-line" />
      <polyline
        points="120,60 138,48 156,54 174,34 192,42 210,28"
        pathLength={100}
        className="dashboard-blueprint-line dashboard-blueprint-trendline"
      />
      <circle cx="120" cy="60" r="2.4" className="dashboard-blueprint-dot dashboard-blueprint-dot-1" />
      <circle cx="156" cy="54" r="2.4" className="dashboard-blueprint-dot dashboard-blueprint-dot-2" />
      <circle cx="192" cy="42" r="2.4" className="dashboard-blueprint-dot dashboard-blueprint-dot-3" />
      <circle cx="210" cy="28" r="2.4" className="dashboard-blueprint-dot dashboard-blueprint-dot-4" />

      {/* Donut chart panel */}
      <rect x="20" y="84" width="70" height="46" rx="2" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-panel-donut" />
      <circle cx="55" cy="107" r="16" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-donut-ring" />
      <path d="M 55 107 L 55 91" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-donut-slice-1" />
      <path d="M 55 107 L 68 116" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-donut-slice-2" />

      {/* Horizontal bar-list panel */}
      <rect x="98" y="84" width="122" height="46" rx="2" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-panel-hbars" />
      <rect x="108" y="94" width="70" height="6" rx="3" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-hbar-1" />
      <rect x="108" y="106" width="94" height="6" rx="3" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-hbar-2" />
      <rect x="108" y="118" width="50" height="6" rx="3" pathLength={100} className="dashboard-blueprint-line dashboard-blueprint-hbar-3" />
    </svg>
  );
}

export function DashboardLoadingSkeleton({ title, subtitle }) {
  return (
    <div className="dashboard-loading-skeleton">
      <DashboardBlueprint />
      <div>
        <p className="dashboard-loading-title">{title ?? <Trans>Building your dashboard...</Trans>}</p>
        <p className="dashboard-loading-subtitle">{subtitle ?? <Trans>Fetching your data</Trans>}</p>
      </div>
    </div>
  );
}

function Dashboard({
  guestToken,
  loadingGuestToken,
  errorLoadingGuestToken,
  fetchToken,
  height,
  hideTitle,
  hideTab,
  hideChartControls,
  filters,
  urlParams,
}) {
  return (
    <>
      {loadingGuestToken ? (
        <DashboardLoadingSkeleton />
      ) : guestToken ? (
        <SupersetDashboard
          embeddedUuid={guestToken?.embeddedUuid}
          initialToken={guestToken?.token}
          fetchToken={fetchToken}
          url={guestToken?.url}
          height={height}
          hideTitle={hideTitle}
          hideTab={hideTab}
          hideChartControls={hideChartControls}
          filters={filters}
          urlParams={urlParams}
        />
      ) : (
        <DashboardLoadFailed reason={errorLoadingGuestToken} />
      )}
    </>
  );
}

// embedDashboard() resolving only means the iframe's initial document has loaded - Superset's own
// React app still needs a moment to mount and paint (its loading state or the real dashboard) on
// top of that blank document, so switching the instant it resolves still shows a brief blank flash.
const POST_EMBED_SETTLE_MS = 1000;

// The embed SDK (0.2.0) only schedules its next token refresh after `fetchGuestToken` RESOLVES: one
// rejected re-mint (a network blip, a slow backend, a transient 5xx) ends the refresh chain for good,
// and the embed 401s from the moment the current token expires. So a failed re-mint is retried here,
// with jittered backoff (~1s, 2s, 4s, 8s, 16s), before giving up. The SDK refreshes 5s before expiry,
// so a retry that lands later than that still revives the embed, though Superset's
// "session has encountered an interruption" toast from the gap stays up until dismissed.
const TOKEN_FETCH_ATTEMPTS = 6;

async function fetchTokenWithRetry(fetchToken) {
  const backoff = new ProgressiveBackoff({ initialDelay: 1000, maxDelay: 16000 });
  for (let attempt = 1; ; attempt++) {
    try {
      return await fetchToken();
    } catch (e) {
      if (attempt >= TOKEN_FETCH_ATTEMPTS) {
        console.error(`[embed] guest token re-mint failed after ${attempt} attempts; the dashboard will stop working once the current token expires.`, e);
        throw e;
      }
      console.warn(`[embed] guest token re-mint failed (attempt ${attempt}/${TOKEN_FETCH_ATTEMPTS}), retrying.`, e);
      await backoff.wait();
    }
  }
}

export function SupersetDashboard({ embeddedUuid, initialToken, fetchToken, url, height, hideTitle, hideTab, hideChartControls, filters, urlParams }) {
  // Required, and deliberately loud: the SDK calls it again ~5s before each token expires, and there is
  // no correct fallback. Replaying an already-fetched token just hands back the same expiring string,
  // so the embed would 401 at the 5-minute mark - which is how three callers silently broke before.
  if (typeof fetchToken !== 'function') {
    throw new Error('SupersetDashboard requires a `fetchToken` that re-mints the guest token.');
  }

  const ref = useRef();
  const readyTimeoutRef = useRef();
  const [dashboard, setDashboard] = useState();
  const [ready, setReady] = useState(false);
  const [innerHeight, setInnerHeight] = useState(height);
  const fitToHeight = !height;
  const uweTheme = useTheme();
  const { isDarkMode } = useDarkMode();
  const [logoDataUri, setLogoDataUri] = useState();

  // The token the caller already fetched to render us (callers need one up front for the embedded uuid,
  // url and their loading/error states). The SDK's first `fetchGuestToken` call is handed this one
  // instead of minting another, which would double the token requests on every page load. Used once:
  // any later call - a refresh, or a re-embed after filters/urlParams change - mints a fresh token.
  // A ref, not a dependency: consuming it must not re-embed the dashboard.
  const initialTokenRef = useRef(initialToken);

  useEffect(() => {
    let cancelled = false;
    fetchLogoDataUri().then(uri => {
      if (!cancelled) setLogoDataUri(uri);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refCallback = useCallback(
    async refElement => {
      if (!refElement) {
        // Ref teardown ahead of a re-embed (e.g. filters/urlParams changed) - show the loader
        // again until the freshly-mounted iframe finishes loading, same as the initial mount.
        clearTimeout(readyTimeoutRef.current);
        setReady(false);
        return;
      }
      ref.current = refElement;

      const dashboard = await embedDashboard({
        // The embed SDK keys on the EMBEDDED uuid; the backend resolved it from the native uuid and
        // returned it in the token response.
        id: embeddedUuid,
        supersetDomain: url,
        mountPoint: refElement,
        // The embed SDK calls this on mount and again as each token nears expiry (guest tokens default
        // to a 300s TTL). The mount call reuses the caller's token (see `initialTokenRef`); every other
        // call mints, with failures retried (see `fetchTokenWithRetry`).
        fetchGuestToken: () => {
          const preloaded = initialTokenRef.current;
          initialTokenRef.current = undefined;
          return preloaded ? Promise.resolve(preloaded) : fetchTokenWithRetry(fetchToken);
        },
        dashboardUiConfig: {
          // dashboard UI config: hideTitle, hideTab, hideChartControls, filters.visible, filters.expanded (optional), urlParams (optional)
          hideTitle,
          hideTab,
          hideChartControls,
          filters,
          urlParams: urlParams,
        },
        iframeSandboxExtras: ['allow-top-navigation', 'allow-popups-to-escape-sandbox'],
        referrerPolicy: 'origin',
      });

      setDashboard(dashboard);
      readyTimeoutRef.current = setTimeout(() => setReady(true), POST_EMBED_SETTLE_MS);
    },
    [embeddedUuid, fetchToken, hideTitle, hideTab, hideChartControls, filters, urlParams]
  );

  useEffect(() => () => clearTimeout(readyTimeoutRef.current), []);

  // Both variants now carry the same reconstructed brand fields (colors, radius, font, logo,
  // spinner), so light and dark are handled identically here — just pick whichever matches
  // OCIF's current toggle state and push it as `theme_default`.
  useEffect(() => {
    if (!dashboard) return;
    dashboard.setThemeConfig({
      theme_default: isDarkMode ? buildDarkThemeDefault(logoDataUri) : buildLightThemeDefault(uweTheme, logoDataUri),
    });
  }, [dashboard, isDarkMode, uweTheme, logoDataUri]);

  return (
    <DashboardWrapper>
      {!ready && (
        <LoaderOverlay>
          <DashboardLoadingSkeleton />
        </LoaderOverlay>
      )}
      <DashboardContainer ref={refCallback} style={{ visibility: ready ? 'visible' : 'hidden' }}>
        Dashboard
      </DashboardContainer>
    </DashboardWrapper>
  );
}

const DashboardWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
`;

const DashboardContainer = styled.div`
  height: 100%;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export default Dashboard;
